import { useState } from "react";
import { useAppDispatch } from "../../../../../store/hooks"
import {
    addStatusSaga,
    updateStatusSaga,
    deleteStatusSaga,
} from "../../../../../store/workspace/workspaceSlice"
import FormColumn from "@/components/ui/Form/components/FormColumn/FormColumn.tsx";
import Button from "../../../../ui/Button/Button.tsx";
import IconPlus from "../../../../../assets/icons/icon-plus.svg?react"
import FormRow from "@/components/ui/Form/components/FormRow/FormRow.tsx";
import StatusNameInput from "./components/StatusNameInput/StatusNameInput";
import InputIcon from "../../../../ui/InputIcon/InputIcon.tsx";
import DropdownWrapper from "@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper.tsx";
import Dropdown from "../../../../ui/Dropdown/Dropdown.tsx";
import DropdownIcons from "@/components/ui/Dropdown/components/DropdownIcons/DropdownIcons.tsx";
import DropdownItem from "@/components/ui/Dropdown/components/DropdownItem/DropdownItem.tsx";
import IconButton from "@/components/ui/IconButton/IconButton.tsx";
import OptionsIcon from '@/assets/icons/icon-options.svg?react';
import IconMoveUp from "../../../../../assets/icons/icon-move-up.svg?react";
import IconMoveDown from "../../../../../assets/icons/icon-move-down.svg?react";
import { useWorkspace } from "../../../../../contexts/WorkspaceContext.tsx";
import { STATUS_COLORS } from "../../../../../const/colors.ts";
import WorkspaceSettingsTitle
    from "@/components/modals/WorkspaceSettingsModal/components/WorkspaceSettingsTitle/WorkspaceSettingsTitle.tsx";
import FormTitle from "@/components/ui/Form/components/FormTitle/FormTitle.tsx";
import FormDivider from "@/components/ui/Form/components/FormDivider/FormDivider.tsx";

const WorkspaceSettingsModalWorkflow = () => {
    const dispatch = useAppDispatch()
    const { statuses } = useWorkspace()
    const [openColorDropdownId, setOpenColorDropdownId] = useState<string | null>(null)
    const [openOptionsDropdownId, setOpenOptionsDropdownId] = useState<string | null>(null)

    const handleRemoveStatus = (id: string) => {
        dispatch(deleteStatusSaga(id))
        setOpenOptionsDropdownId(null)
    }

    const handleAddStatus = () => {
        dispatch(addStatusSaga({ name: 'New status', color: '#6B7280' }))
    }

    const handleOpenColorDropdown = (id: string, disabled: boolean) => {
        if (!disabled) {
            setOpenColorDropdownId(id)
        }
    }

    const handleCloseColorDropdown = () => {
        setOpenColorDropdownId(null)
    }

    const handleChangeColor = (statusId: string, color: string) => {
        dispatch(updateStatusSaga({ statusId, data: { color } }))
        handleCloseColorDropdown()
    }

    const getColorIcons = (statusId: string) => {
        return STATUS_COLORS.map((hex) => ({
            icon: <div style={{ width: 16, height: 16, border: '1px solid rgba(0, 0, 0, 0.16)', borderRadius: '50%', backgroundColor: hex }} />,
            onClick: () => handleChangeColor(statusId, hex)
        }))
    }

    const handleMoveUp = (statusId: string) => {
        const idx = statuses.findIndex(s => s.id === statusId)
        if (idx <= 1) return
        const current = statuses[idx]
        const prev = statuses[idx - 1]
        dispatch(updateStatusSaga({ statusId: current.id, data: { order: prev.order } }))
        dispatch(updateStatusSaga({ statusId: prev.id, data: { order: current.order } }))
        setOpenOptionsDropdownId(null)
    }

    const handleMoveDown = (statusId: string) => {
        const idx = statuses.findIndex(s => s.id === statusId)
        if (idx >= statuses.length - 2) return
        const current = statuses[idx]
        const next = statuses[idx + 1]
        dispatch(updateStatusSaga({ statusId: current.id, data: { order: next.order } }))
        dispatch(updateStatusSaga({ statusId: next.id, data: { order: current.order } }))
        setOpenOptionsDropdownId(null)
    }

    return (
        <>
            <WorkspaceSettingsTitle>Statuses</WorkspaceSettingsTitle>
            <FormDivider />
            <FormColumn gap={24}>
                <FormColumn gap={16}>
                    <FormTitle>Task statuses</FormTitle>
                    <FormColumn gap={16}>
                        {statuses.map((status, idx) => {
                            const isSystem = !!status.systemType
                            const canMoveUp = !isSystem && idx > 1
                            const canMoveDown = !isSystem && idx < statuses.length - 2

                            return (
                                <FormRow key={status.id}>
                                    <DropdownWrapper>
                                        <InputIcon
                                            disabled={isSystem}
                                            icon={
                                                status.color?.startsWith('#')
                                                    ? <div style={{ width: 16, height: 16, border: '1px solid rgba(0, 0, 0, 0.16)', borderRadius: '50%', backgroundColor: status.color }} />
                                                    : <div style={{ width: 16, height: 16, border: '1px solid rgba(0, 0, 0, 0.16)', borderRadius: '50%', backgroundColor: 'var(--white)' }} />
                                            }
                                            onClick={() => handleOpenColorDropdown(status.id, isSystem)}
                                        />
                                        <Dropdown
                                            isOpen={openColorDropdownId === status.id}
                                            onClose={handleCloseColorDropdown}
                                            width="small"
                                        >
                                            <DropdownIcons icons={getColorIcons(status.id)} />
                                        </Dropdown>
                                    </DropdownWrapper>
                                    <StatusNameInput
                                        statusId={status.id}
                                        name={status.name}
                                        disabled={isSystem}
                                    />
                                    <DropdownWrapper>
                                        <IconButton
                                            icon={<OptionsIcon />}
                                            disabled={isSystem}
                                            onClick={() => setOpenOptionsDropdownId(
                                                openOptionsDropdownId === status.id ? null : status.id
                                            )}
                                        />
                                        <Dropdown
                                            isOpen={openOptionsDropdownId === status.id}
                                            onClose={() => setOpenOptionsDropdownId(null)}
                                            direction="down"
                                        >
                                            <DropdownItem
                                                icon={<IconMoveUp />}
                                                title="Move up"
                                                disabled={!canMoveUp}
                                                onClick={() => handleMoveUp(status.id)}
                                            />
                                            <DropdownItem
                                                icon={<IconMoveDown />}
                                                title="Move down"
                                                disabled={!canMoveDown}
                                                onClick={() => handleMoveDown(status.id)}
                                            />
                                            <DropdownItem
                                                title="Delete"
                                                danger
                                                onClick={() => handleRemoveStatus(status.id)}
                                            />
                                        </Dropdown>
                                    </DropdownWrapper>
                                </FormRow>
                            )
                        })}
                    </FormColumn>
                </FormColumn>
                <FormRow align={"start"}>
                    <Button icon={<IconPlus/>} type={"white"} title={"Add status"} onClick={handleAddStatus}/>
                </FormRow>
            </FormColumn>

        </>
    )
}

export default WorkspaceSettingsModalWorkflow
